import type { ICartStore } from "./common-cart/CommonCartStore";
import type { CreditCheckStoreImpl } from "./CreditCheckStore";
import { CreditCheckStore } from "./CreditCheckStore";
import { trackUiError } from "../utils/TrackingUtil";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import { calculatePiecesFromWeight } from "../shared/amount-chooser-panel/index";
import type { CommonCartItem } from "../model/CommonCartTypes";
import type { CreditLimitPolicy } from "../model/StoreInformation";
import { getRemainingCredit } from "./CheckoutTypes";
import { Store } from "../utils/Store";
import { fetchCreditLimitPolicy } from "../server/ServerRequestsInternal";
import { CustomerCartStore } from "./customer-cart/CustomerCartStore";
import { SavedCartStore } from "./customer-cart/SavedCartStore";
import { AnonCartStore } from "./anon-cart/AnonCartStore";
import deepEquals from "fast-deep-equal/es6";
const MAX_ITEMS = 500;
const MAX_COMMENT_LENGTH = 200;
type CartError =
	| "TOO_MANY_ITEMS"
	| "ITEM_ERRORS"
	| "ENFORCE_AVAILABLE_CREDIT_LIMIT_EXCEEDED"
	| "CUSTOMER_CHECKOUT_BLOCKED";
export type ItemError =
	| "NOT_FOUND"
	| "LICENSE_MISSING"
	| "RESTRICTED"
	| "ANONYMOUS"
	| "AMOUNT_NOT_ALLOWED"
	| "MINOQ_NOT_REACHED"
	| "MAXOQ_EXCEEDED"
	| "MULTIOQ_VIOLATED"
	| "ZERO_QUANTITY"
	| "COMMENT_TOO_LONG"
	| "UNKNOWN"
	| "ITEM_IS_UNAVAILABLE"
	| "ITEM_NOT_BUYABLE"
	| "ITEM_MISSING_PRICES";
export type ErrorState = {
	cartErrors: Set<CartError>;
	itemErrors: Map<string, Set<ItemError>>;
};
export type CartNotificationStoreState = {
	errorState: ErrorState;
	creditLimitPolicy: CreditLimitPolicy | null | undefined;
};
export class CartNotificationStoreImpl extends Store {
	_state: CartNotificationStoreState;
	_cartStore: ICartStore;
	_creditCheckStore: CreditCheckStoreImpl;

	constructor(cartStore: ICartStore, creditCheckStore: CreditCheckStoreImpl) {
		super();
		this._cartStore = cartStore;
		this._creditCheckStore = creditCheckStore;
		this._state = {
			errorState: {
				cartErrors: new Set(),
				itemErrors: new Map(),
			},
			creditLimitPolicy: null,
		};
		this.listenTo(this._cartStore, () => this.handleUpdate());
		this.listenTo(this._creditCheckStore, () => {
			this.handleUpdate().catch((error) => {
				console.error("handleUpdate in CartNotificationStore failed with ", error);
			});
		});
	}

	async getCreditLimitPolicy(): Promise<CreditLimitPolicy> {
		// relaxed standard case as per business logic
		const fallbackPolicy = "DO_NOT_ENFORCE";
		const country = SessionStore.getCountry();
		if (this._state.creditLimitPolicy) return this._state.creditLimitPolicy;

		try {
			this._state = { ...this._state, creditLimitPolicy: fallbackPolicy };
			const creditLimitPolicy = (
				await fetchCreditLimitPolicy({
					country,
				})
			).policy;
			this._state = { ...this._state, creditLimitPolicy: creditLimitPolicy };
			return creditLimitPolicy;
		} catch (e) {
			trackUiError("CartNotificationStore", "Failed to fetch credit limit policy.");
			// no retries
			return fallbackPolicy;
		}
	}

	async isOverCreditLimitAndShouldEnforce(): Promise<boolean> {
		if (!SessionStore.loggedIn()) return false;
		const policy = await this.getCreditLimitPolicy();
		if (policy !== "ENFORCE") return false;

		const creditState = this._creditCheckStore.getState();

		const cart = this._cartStore.getCart();

		if (!creditState || !cart) {
			return false;
		}

		const remainingCredit = getRemainingCredit(creditState.creditCheckInfo);
		return !!(
			cart.totalPrices?.total?.gross &&
			!isNaN(cart.totalPrices.total.gross.amount) &&
			remainingCredit &&
			!isNaN(remainingCredit.amount) &&
			remainingCredit.amount - cart.totalPrices.total.gross.amount < 0
		);
	}

	async handleUpdate(): Promise<void> {
		const errorState = await this.calculateErrors();

		if (!deepEquals(errorState, this._state.errorState)) {
			this._state.errorState = errorState;
			this.trigger();
		}
	}

	async calculateErrors(): Promise<ErrorState> {
		const itemErrors: Map<string, Set<ItemError>> = new Map();
		const cartErrors: Set<CartError> = new Set();

		const { customerCheckoutBlocked = false } = this._creditCheckStore.getState() || {};
		if (customerCheckoutBlocked) {
			cartErrors.add("CUSTOMER_CHECKOUT_BLOCKED" as CartError);
		}

		const cart = this._cartStore.getCart();

		if (await this.isOverCreditLimitAndShouldEnforce()) {
			cartErrors.add("ENFORCE_AVAILABLE_CREDIT_LIMIT_EXCEEDED");
		}

		if (cart) {
			Object.values(cart.items).forEach((item) => {
				const itemError: Set<ItemError> = new Set([
					...CartNotificationStoreImpl.getCommentErrors(item),
					...CartNotificationStoreImpl.getConfirmedErrors(item),
					...CartNotificationStoreImpl.getStockErrors(item),
					...CartNotificationStoreImpl.getQuantityErrors(item),
					...CartNotificationStoreImpl.getNonFsdItemMissingPriceErrors(item),
				]);
				if (itemError.size > 0) itemErrors.set(item.bundleId, itemError);
			});

			if (itemErrors.size > 0) cartErrors.add("ITEM_ERRORS");

			if (Object.keys(cart.items).length > MAX_ITEMS) {
				cartErrors.add("TOO_MANY_ITEMS");
			}
		}

		return {
			itemErrors,
			cartErrors,
		};
	}

	getErrorState(): ErrorState {
		return this._state.errorState;
	}

	getState(): CartNotificationStoreState {
		return {
			errorState: this.getErrorState(),
			creditLimitPolicy: this._state.creditLimitPolicy,
		};
	}

	static getCommentErrors(item: CommonCartItem): Set<ItemError> {
		const itemError: Set<ItemError> = new Set();

		if (item.comment && item.comment.length > MAX_COMMENT_LENGTH) {
			itemError.add("COMMENT_TOO_LONG");
		}

		return itemError;
	}

	static getStockErrors(item: CommonCartItem): Set<ItemError> {
		const itemError: Set<ItemError> = new Set();
		if (item.missingReasons?.some((it) => ["PENDING", "CACHE_MISS"].includes(it))) return itemError;

		if (item.stockMustBeChecked && typeof item.availableStock === "number" && item.quantity > item.availableStock) {
			if (item.availableStock <= 0) {
				itemError.add("ITEM_IS_UNAVAILABLE");
			} else {
				itemError.add("AMOUNT_NOT_ALLOWED");
			}
		}

		return itemError;
	}

	static getNonFsdItemMissingPriceErrors(item: CommonCartItem): Set<ItemError> {
		const itemError: Set<ItemError> = new Set();

		if (item.selectedFulfillmentType && item.selectedFulfillmentType !== "FSD" && item.prices === null) {
			itemError.add("ITEM_MISSING_PRICES");
		}

		return itemError;
	}

	static getQuantityErrors(item: CommonCartItem): Set<ItemError> {
		const itemError: Set<ItemError> = new Set();
		const quantityRespectingWeightPerPiece = this.computeQuantity(item);

		if (item.quantity === 0) {
			itemError.add("ZERO_QUANTITY");
		}

		if (item.minOrderQuantity && item.minOrderQuantity > quantityRespectingWeightPerPiece) {
			itemError.add("MINOQ_NOT_REACHED");
		}

		const maxOrderQuantity = item.maxOrderQuantity;

		if (typeof maxOrderQuantity === "number") {
			if (maxOrderQuantity === 0) {
				// Showing the MaxOQ message would be more confusing than helpful for the user
				// in this (misconfiguration) case.
				itemError.add("ITEM_NOT_BUYABLE");
			} else if (maxOrderQuantity < quantityRespectingWeightPerPiece) {
				itemError.add("MAXOQ_EXCEEDED");
			}
		}

		if (
			item.useMinQuantityMultiplier &&
			item.minOrderQuantity &&
			item.minOrderQuantity > 1 &&
			quantityRespectingWeightPerPiece % item.minOrderQuantity !== 0
		) {
			itemError.add("MULTIOQ_VIOLATED");
		}

		return itemError;
	}

	static computeQuantity(item: CommonCartItem): number {
		const weightPerPiece = item.weightPerPiece?.value;
		return weightPerPiece ? calculatePiecesFromWeight(item.quantity, weightPerPiece) : item.quantity;
	}

	static getConfirmedErrors(item: CommonCartItem): Set<ItemError> {
		const itemError: Set<ItemError> = new Set();
		if (item.missingReasons?.some((it) => ["PENDING", "CACHE_MISS"].includes(it))) return itemError;
		if (!item.orderable) itemError.add("ITEM_NOT_BUYABLE");
		if (!item.selectedFulfillmentType) itemError.add("UNKNOWN");

		if (item.missingReasons) {
			item.missingReasons.forEach((reason) => {
				const typesafeReason = CartNotificationStoreImpl.reasonToItemError(reason);
				if (typesafeReason) itemError.add(typesafeReason);
			});
		}

		if (!item.orderable) itemError.add("ITEM_NOT_BUYABLE");
		return itemError;
	}

	static reasonToItemError(reason: string | null | undefined): ItemError | null | undefined {
		switch (reason) {
			case "NOT_FOUND":
				return "NOT_FOUND";

			case "LICENSE_MISSING":
				return "LICENSE_MISSING";

			case "RESTRICTED":
				return "RESTRICTED";

			default:
				return null;
		}
	}
}
export const CustomerCartNotificationStore = new CartNotificationStoreImpl(CustomerCartStore, CreditCheckStore);
export const SavedCartNotificationStore = new CartNotificationStoreImpl(SavedCartStore, CreditCheckStore);
export const AnonCartNotificationStore = new CartNotificationStoreImpl(AnonCartStore, CreditCheckStore);
